/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus, Role } from '../../../../ts';
import { InstanceLimits, Plan } from '../../../../ts/instance';
import { InstanceSlice } from '../../../../ts/state';
import {
  getInstance,
  updateInstance,
  updateInstancePreferences,
  updateUserPreferences
} from './actions';

// Initial State
export const initialState: InstanceSlice = Object.freeze({
  getStatus: ApiStatus.IDLE,
  getError: null,
  lastFetched: null,
  updateStatus: ApiStatus.IDLE,
  updateError: null,
  updateUserPreferencesStatus: ApiStatus.IDLE,
  updateUserPreferencesError: null,
  updateInstancePreferencesStatus: ApiStatus.IDLE,
  updateInstancePreferencesError: null,
  instance: null,
  instance_role: Role.READER,
  limits: {} as InstanceLimits,
  plan: {} as Plan,
  tooling_data: {},
  instance_custom_roles: [],
  new_ia: false,
  user_preferences: {},
  instance_preferences: {}
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: InstanceSlice): InstanceSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice + Reducer
const slice = createSlice({
  name: 'instance',
  initialState: computeState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getInstance.pending, state => {
        state.getStatus = ApiStatus.LOADING;
        state.getError = null;
      })
      .addCase(getInstance.fulfilled, (state, action) => {
        state.getStatus = ApiStatus.SUCCEEDED;
        state.getError = null;
        state.lastFetched = Date.now();
        Object.assign(state, _.omit(action.payload, ['featureFlags']));
      })
      .addCase(getInstance.rejected, (state, action) => {
        state.getStatus = ApiStatus.ERROR;
        state.getError = action.error;
      })
      .addCase(updateInstance.pending, state => {
        state.updateStatus = ApiStatus.LOADING;
        state.updateError = null;
      })
      .addCase(updateInstance.fulfilled, (state, action) => {
        state.updateStatus = ApiStatus.SUCCEEDED;
        state.updateError = null;
        state.lastFetched = Date.now();
        Object.assign(state, _.omit(action.payload, ['featureFlags']));
      })
      .addCase(updateInstance.rejected, (state, action) => {
        state.updateStatus = ApiStatus.ERROR;
        state.updateError = action.error;
      })
      .addCase(updateUserPreferences.pending, state => {
        state.updateUserPreferencesStatus = ApiStatus.LOADING;
        state.updateUserPreferencesError = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.updateUserPreferencesStatus = ApiStatus.SUCCEEDED;
        state.updateUserPreferencesError = null;
        Object.assign(state, _.omit(action.payload, ['featureFlags']));
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.updateUserPreferencesStatus = ApiStatus.ERROR;
        state.updateUserPreferencesError = action.error;
      })
      .addCase(updateInstancePreferences.pending, state => {
        state.updateInstancePreferencesStatus = ApiStatus.LOADING;
        state.updateInstancePreferencesError = null;
      })
      .addCase(updateInstancePreferences.fulfilled, (state, action) => {
        state.updateInstancePreferencesStatus = ApiStatus.SUCCEEDED;
        state.updateInstancePreferencesError = null;

        // Update the instance preference section with the values received
        // in the action payload.
        Object.assign(state.instance_preferences, action.payload);
      })
      .addCase(updateInstancePreferences.rejected, (state, action) => {
        state.updateInstancePreferencesStatus = ApiStatus.ERROR;
        state.updateInstancePreferencesError = action.error;
      });
  }
});

export default slice.reducer;
